import { motion } from "framer-motion";
import { ArrowRight, Linkedin } from "lucide-react";
import { profile } from "../data/profile";
import { CountUp } from "./CountUp";

const HERO_IMG =
  "https://images.pexels.com/photos/19438563/pexels-photo-19438563.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720";

const LOGO =
  "https://customer-assets.emergentagent.com/job_vijay-pandya-site/artifacts/avlesj29_dr%20pandya%20f%20logo.png";

const REAL_PORTRAIT = "/assets/vijay-pandya.jpg";

export const Hero = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="hero" className="relative pt-28 pb-16 sm:pt-32 sm:pb-20 md:pt-40 md:pb-28 overflow-hidden">
      <div className="absolute top-24 -right-32 h-96 w-96 rounded-full bg-teal/10 blur-3xl pointer-events-none animate-blob" />
      <div className="absolute top-1/2 -left-40 h-80 w-80 rounded-full bg-gold/10 blur-3xl pointer-events-none animate-blob hidden sm:block" style={{ animationDelay: "4s" }} />
      <div className="absolute bottom-0 right-1/4 h-72 w-72 rounded-full bg-navy/5 blur-3xl pointer-events-none animate-blob hidden sm:block" style={{ animationDelay: "8s" }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="overline">Healthcare Professional</span>
              <h1 className="sr-only">{profile.name} — Healthcare Leader</h1>
              <img
                src={LOGO}
                alt="Dr. Vijay Pandya — Healthcare Leader"
                data-testid="hero-logo"
                className="mt-5 w-[300px] sm:w-[380px] lg:w-[440px] h-auto mix-blend-multiply -ml-2"
              />
              <p className="mt-4 text-base sm:text-lg text-gold font-plex tracking-wide">
                {profile.qualifications}
              </p>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-justify" style={{ color: "#4B5A69" }}>
                {profile.intro}
              </p>
              <div className="mt-9 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
                <button
                  data-testid="hero-consult-btn"
                  onClick={() => go("contact")}
                  className="btn-shine group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md bg-navy text-bone font-medium hover:bg-teal transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-navy/20"
                >
                  Book Appointment
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <a
                  data-testid="hero-linkedin-link"
                  href={profile.contact.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-md border border-[#cdd2cb] text-navy font-medium hover:border-navy hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Linkedin size={18} className="animate-float" />
                  Connect on LinkedIn
                </a>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="relative"
            >
              <div className="relative mx-auto w-[270px] sm:w-[360px] lg:w-[420px] max-w-full aspect-square animate-float">
                <div className="absolute inset-0 rounded-full border-2 border-dashed border-gold/40 animate-spin-slow" />
                <div className="absolute inset-3 rounded-full overflow-hidden bg-white border border-[#E2E5E1] shadow-[0_30px_60px_-25px_rgba(11,29,46,0.4)] img-zoom">
                  <img
                    src={REAL_PORTRAIT}
                    alt="Dr. Vijay P. Pandya"
                    className="w-full h-full object-cover"
                  />
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.7, type: "spring" }}
                  className="absolute bottom-2 -left-3 sm:-left-5 bg-navy text-bone rounded-xl px-5 py-3 shadow-xl animate-pulse-ring"
                >
                  <p className="font-display text-2xl sm:text-3xl font-bold">
                    <CountUp value="20+" />
                  </p>
                  <p className="text-[0.6rem] sm:text-xs font-plex uppercase tracking-widest text-gold">Years Experience</p>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          className="mt-12 sm:mt-16 lg:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#E2E5E1] border border-[#E2E5E1] rounded-xl overflow-hidden"
        >
          {profile.stats.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`stat-${s.label}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="group bg-bone px-6 py-7 text-center hover:bg-white transition-colors duration-300"
            >
              <p className="font-display text-3xl sm:text-4xl font-bold text-navy group-hover:text-teal transition-colors">
                <CountUp value={s.value} />
              </p>
              <p className="mt-2 text-xs sm:text-sm leading-snug" style={{ color: "#4B5A69" }}>
                {s.label}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
